import {
  ActivitiesSubsectionNames,
  AREA_ID_PREFIX,
  AutoLockFormsLevelType,
  LeadSubsectionNames,
  LoanEditorSectionNames,
  TasksSubsectionNames,
} from './autoLockForms.constants';

// From Sep 2025 until LEN2-499 the Lead, Activities and Tasks sections had no name (a merge
// dropped their LoanEditorSectionNames keys), so the ids the tree built for them read
// `undefined-<subsection>-…`, `subsection:undefined-…`, `tab:undefined-…` and
// `section:undefined` — and auto-lock rules and checklist links stored those ids. The
// helpers below map such a stored id onto the restored section name; ids are rewritten
// for good the next time their rule or checklist item is saved.
const LEGACY_SECTION_NAME = 'undefined';

const SECTION_BY_LEGACY_SUBSECTION = {
  [LeadSubsectionNames.LeadDetails]: LoanEditorSectionNames.LEAD,
  [ActivitiesSubsectionNames.ACTIVITIES]: LoanEditorSectionNames.ACTIVITIES,
  [TasksSubsectionNames.TASKS]: LoanEditorSectionNames.TASKS,
};

// `undefined-<subsection>` at the start of an id or right after a `prefix:`.
const LEGACY_PATH_PATTERN = new RegExp(
  `(^|:)${LEGACY_SECTION_NAME}-(${Object.keys(SECTION_BY_LEGACY_SUBSECTION).join('|')})(?=-|$)`,
);

const SECTION_AREA_ID_PREFIX = AREA_ID_PREFIX[AutoLockFormsLevelType.SECTION];

const LEGACY_SECTION_AREA_ID = `${SECTION_AREA_ID_PREFIX}${LEGACY_SECTION_NAME}`;

// `section:undefined` as the whole id or after a `prefix:` (never `subsection:undefined`).
const LEGACY_SECTION_AREA_ID_PATTERN = new RegExp(`(^|:)${LEGACY_SECTION_AREA_ID}$`);

const RESTORED_SECTION_AREA_IDS = Object.values(SECTION_BY_LEGACY_SUBSECTION).map(
  section => `${SECTION_AREA_ID_PREFIX}${section}`,
);

// The tree resolved the shared `section:undefined` to its last writer, Tasks — keep that.
const LEGACY_SECTION_AREA_ID_TARGET = `${SECTION_AREA_ID_PREFIX}${LoanEditorSectionNames.TASKS}`;

const replaceLegacySectionAreaId = (id, sectionAreaId) =>
  `${id.slice(0, -LEGACY_SECTION_AREA_ID.length)}${sectionAreaId}`;

/**
 * Maps one stored appFormsTree id (field path, form id or prefixed area id, optionally
 * `hidden:`-prefixed) onto the restored Lead/Activities/Tasks section names; any other id
 * is returned unchanged.
 *
 * @param {string} id
 * @returns {string}
 */
export const normalizeLegacyAppFormsTreeId = id => {
  if (LEGACY_SECTION_AREA_ID_PATTERN.test(id)) {
    return replaceLegacySectionAreaId(id, LEGACY_SECTION_AREA_ID_TARGET);
  }

  return id.replace(
    LEGACY_PATH_PATTERN,
    (__, prefix, subsection) =>
      `${prefix}${SECTION_BY_LEGACY_SUBSECTION[subsection]}-${subsection}`,
  );
};

/**
 * Like `normalizeLegacyAppFormsTreeId` for a list of rule ids — except the shared
 * `section:undefined` expands to all three sections, since a rule on it applied to all
 * three.
 *
 * @param {string[]} ids
 * @returns {string[]}
 */
export const normalizeLegacyAppFormsTreeIds = ids =>
  ids.flatMap(id =>
    LEGACY_SECTION_AREA_ID_PATTERN.test(id)
      ? RESTORED_SECTION_AREA_IDS.map(sectionAreaId =>
          replaceLegacySectionAreaId(id, sectionAreaId),
        )
      : [normalizeLegacyAppFormsTreeId(id)],
  );

/**
 * The appFormsTree id of a field (`section-subsection-tab-form-field`) — what checklist
 * links store and what "Go to field" looks up as `[data-field-id="…"]`. Mark a rendered
 * field with it when its DOM `name` is not the tree's field name (LEN2-499).
 *
 * @param {{ section: string, subsection: string, tab: string, form: string, field: string }} parts
 * @returns {string}
 */
export const getAppFormsTreeFieldId = ({ section, subsection, tab, form, field }) =>
  [section, subsection, tab, form, field].join('-');
